import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  RiAddLine,
  RiCloseLine,
  RiSearchLine,
  RiDeleteBinLine,
} from "react-icons/ri";
import { MdClose } from "react-icons/md";
import toast from "react-hot-toast";
import Swal from "sweetalert2";

const EventCardSkeleton = () => (
  <div className="bg-white rounded-xl shadow-md border p-4 animate-pulse">
    <div className="h-5 bg-gray-200 rounded w-3/4"></div>
  </div>
);

const ChatbotInquiries = () => {
  const [events, setEvents] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [bookings, setBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");

  const [formData, setFormData] = useState({
    title: "",
    description: "",
  });

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/chatbotevent/get-all`
      );
      setEvents(res.data.data || []);
    } catch (err) {
      console.error(err);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchBookings = async () => {
    try {
      setLoadingBookings(true);
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/chatbotevent/get-all-bookings`
      );
      setBookings(res.data.data || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch bookings");
      setBookings([]);
    } finally {
      setLoadingBookings(false);
    }
  };

  const deleteBooking = async (bookingId) => {
    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#10B981",
        cancelButtonColor: "#EF4444",
        confirmButtonText: "Yes, delete it!",
        cancelButtonText: "Cancel",
      });

      if (result.isConfirmed) {
        await axios.delete(
          `${process.env.REACT_APP_API_URL}/chatbotevent/delete-booking/${bookingId}`
        );

        const updated = bookings.filter((b) => b._id !== bookingId);
        setBookings(updated);

        Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: "The booking has been deleted successfully.",
          confirmButtonColor: "#10B981",
          background: "#fff",
        });
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete booking");
    }
  };

  useEffect(() => {
    fetchEvents();
    fetchBookings();
  }, []);

  const filteredEvents = events.filter((event) => {
    const matchesSearch =
      searchQuery === "" ||
      event?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      event?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase());

    return matchesSearch;
  });

  // Get unique categories from events
  const categories = ["all", ...new Set(events.map((event) => event?.title))];

  // Filter bookings based on selected category
  const getFilteredBookings = () => {
    if (selectedCategory === "all") {
      return bookings;
    }
    const selectedEvent = events.find((e) => e?.title === selectedCategory);
    if (selectedEvent) {
      return bookings?.filter(
        (booking) => booking?.event?._id === selectedEvent?._id
      );
    }
    return [];
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const openNewModal = () => {
    setFormData({
      title: "",
      description: "",
    });
    setIsModalOpen(true);
  };

  const deleteEvent = async (_id) => {
    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#10B981",
        cancelButtonColor: "#EF4444",
        confirmButtonText: "Yes, delete it!",
        cancelButtonText: "Cancel",
      });

      if (result.isConfirmed) {
        await axios.delete(
          `${process.env.REACT_APP_API_URL}/chatbotevent/delete/${_id}`
        );

        const updated = events.filter((e) => e._id !== _id);
        setEvents(updated);

        Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: "The chatbot inquiry has been deleted successfully.",
          confirmButtonColor: "#10B981",
          background: "#fff",
        });
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete chatbot inquiry");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      await axios.post(
        `${process.env.REACT_APP_API_URL}/chatbotevent/create`,
        formData
      );
      toast.success("Chatbot inquiry created successfully!");
      setIsModalOpen(false);
      fetchEvents();
    } catch (err) {
      console.error(err);
      toast.error("Failed to save chatbot inquiry");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getEventBookings = (eventId) => {
    return bookings.filter((booking) => booking.event._id === eventId);
  };

  return (
    <div className="min-h-screen bg-transparent p-6 tracking-wide">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-heading font-bold bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor bg-clip-text text-transparent">
          Chatbot Inquiries
        </h1>
        <button
          onClick={openNewModal}
          className="bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor text-white py-2 px-4 rounded-xl flex items-center shadow-lg hover:shadow-xl transition-shadow"
        >
          <RiAddLine className="mr-2" />
          Add Inquiry Menu
        </button>
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-2xl shadow-lg border border-primaryTextColor p-6 mb-8">
        <div>
          <label className="block text-sm font-semibold text-secondaryTextColor mb-2">
            Search Inquiries
          </label>
          <div className="relative">
            <RiSearchLine className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by title or description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full border border-gray-300 rounded-lg p-3 pl-10 focus:outline-none focus:ring-2 focus:ring-primaryBtnColor"
            />
          </div>
        </div>
      </div>

      {/* List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {loading ? (
          Array.from({ length: 6 }).map((_, i) => <EventCardSkeleton key={i} />)
        ) : filteredEvents.length > 0 ? (
          filteredEvents.map((event) => (
            <div
              key={event?._id}
              className="bg-white rounded-xl shadow-md border border-gray-200 p-4 hover:shadow-lg transition-shadow relative group"
            >
              <div className="flex items-start justify-between">
                <h2 className="text-base font-bold text-primaryTextColor font-heading capitalize pr-8">
                  {event?.title}
                </h2>
                <div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => deleteEvent(event?._id)}
                    className="p-1.5 bg-red-50 rounded-full text-red-500 hover:bg-red-500 hover:text-white transition-colors"
                    title="Delete Inquiry"
                  >
                    <MdClose size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full text-center py-12">
            <p className="text-gray-500 text-lg">No chatbot inquiries found</p>
          </div>
        )}
      </div>

      {/* Category Tabs */}
      <div className="mb-8">
        <div className="flex flex-wrap gap-2 bg-white rounded-2xl shadow-lg border border-primaryTextColor p-4">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-6 py-2.5 rounded-xl font-semibold transition-all ${
                selectedCategory === category
                  ? "bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor text-white shadow-md"
                  : "bg-white text-gray-700 border border-gray-300 hover:border-primaryBtnColor hover:text-primaryBtnColor"
              }`}
            >
              {category === "all" ? "All Menus" : category}
            </button>
          ))}
        </div>
      </div>

      {/* All Bookings Section */}
      <div className="mt-12" id="all-bookings-section">
        <h2 className="text-2xl font-heading font-bold bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor bg-clip-text text-transparent mb-6">
          {selectedCategory === "all"
            ? "All Bookings"
            : `Bookings for ${selectedCategory}`}
        </h2>

        {loadingBookings ? (
          <div className="flex justify-center py-12">
            <div className="spinner"></div>
          </div>
        ) : getFilteredBookings().length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {getFilteredBookings().map((booking) => (
              <div
                key={booking._id}
                className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-shadow relative group"
              >
                <button
                  onClick={() => deleteBooking(booking._id)}
                  className="absolute top-4 right-4 p-1.5 bg-red-50 rounded-full text-red-500 hover:bg-red-500 hover:text-white transition-colors opacity-0 group-hover:opacity-100"
                  title="Delete Booking"
                >
                  <RiDeleteBinLine size={16} />
                </button>

                <div className="mb-3 pb-3 border-b border-gray-200">
                  <p className="text-sm text-gray-500 font-semibold">
                    Category
                  </p>
                  <p className="text-base text-primaryBtnColor font-bold">
                    {booking?.event?.title}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pr-10">
                  <div>
                    <p className="text-sm text-gray-600 font-semibold">Name</p>
                    <p className="text-base text-primaryTextColor">
                      {booking.name}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 font-semibold">Phone</p>
                    <p className="text-base text-primaryTextColor">
                      {booking.phone}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 font-semibold">Email</p>
                    <p className="text-base text-primaryTextColor">
                      {booking.email}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 font-semibold">
                      Address
                    </p>
                    <p className="text-base text-primaryTextColor">
                      {booking.address}
                    </p>
                  </div>
                  {booking.message && (
                    <div className="col-span-full">
                      <p className="text-sm text-gray-600 font-semibold">
                        Message
                      </p>
                      <p className="text-base text-primaryTextColor">
                        {booking.message}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-12 text-center">
            <p className="text-gray-500 text-lg">
              No bookings found for this category
            </p>
          </div>
        )}
      </div>

      {/* Add Inquiry Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl border border-primaryTextColor shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
              <h2 className="text-xl font-bold text-primaryTextColor">
                Add New Chatbot Inquiry Menu Option
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-500 hover:text-primaryBtnHoverColor"
              >
                <RiCloseLine size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-2">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData?.title}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-primaryBtnColor"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-primaryBtnColor"
                  rows="4"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="bg-gray-200 text-secondaryTextColor py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  disabled={isSubmitting}
                  type="submit"
                  className="bg-primaryBtnColor text-white w-40 flex items-center justify-center py-2 px-4 rounded-lg hover:bg-primaryBtnHoverColor transition-colors disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <div className="spinner w-full"></div>
                  ) : (
                    "Add Inquiry"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatbotInquiries;
